import { Box, Modal, Radio, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import useCommonQuery from "../../store/common/useCommonQuery";
import noPic from "../../assets/no-pic.png";

function SubCategoryModal({ open, setOpen, handleClose, categoryId, subCategoryId }) {
  const [selectedId, setSelectedId] = useState("");
  const [hoveredId, setHoveredId] = useState(null);

  /* Fetch subcategories for the selected category — existing API, unchanged */
  const { data: subcategoryList } = useCommonQuery.Get_subcategory({
    id: categoryId,
    search: "",
    limit: 2000,
    offset: 0,
  });

  const handleChange = (data) => {
    setSelectedId(data.id);
    handleClose(data);
  };

  useEffect(() => {
    setSelectedId(subCategoryId);
  }, [subCategoryId]);

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92vw", sm: "380px" },
          background: "#ffffff",
          boxShadow: "0px 20px 60px rgba(0,0,0,0.22), 0px 4px 16px rgba(0,0,0,0.10)",
          maxHeight: "88vh",
          border: "1px solid #ddeef7",
          overflowY: "auto",
          borderRadius: "22px",
          outline: "none",
        }}
      >
        {/* ── Header — light-blue tint distinguishes subcategory from category popup ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px 20px 12px",
            borderBottom: "1px solid #ddeef7",
            background: "linear-gradient(135deg, #f0f8ff 0%, #e4f2fb 100%)",
            borderRadius: "22px 22px 0 0",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#1565C0", mr: "8px", flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: "15px", fontWeight: "700", color: "#1a1a1a", letterSpacing: "0.3px" }}>
            Select Sub Category
          </Typography>
        </Box>

        {/* ── Card list — subtle blue page tint reinforces subcategory context ── */}
        <Box
          sx={{
            padding: "12px 14px 4px",
            display: "flex",
            flexDirection: "column",
            gap: "9px",
            background: "linear-gradient(180deg, #f7fbff 0%, #ffffff 60%)",
          }}
        >
          {subcategoryList?.data?.results?.map((item) => {
            const isSelected = item.id == selectedId;
            const isHovered = hoveredId === item.id;

            return (
              <div
                key={item.id}
                onClick={() => handleChange(item)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "13px",
                  padding: "10px 12px",
                  borderRadius: "14px",
                  border: isSelected ? "1.5px solid #1565C0" : "1px solid #e4eef7",
                  background: isSelected
                    ? "linear-gradient(135deg, #e3eeff 0%, #cfe0ff 100%)"
                    : isHovered ? "#eef5fb"
                    : "linear-gradient(135deg, #ffffff 0%, #f7fbff 100%)",
                  boxShadow: isSelected
                    ? "0 4px 16px rgba(21,101,192,0.20), 0 1px 4px rgba(21,101,192,0.10)"
                    : isHovered
                    ? "0 6px 16px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)"
                    : "0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
                  cursor: "pointer",
                  transition: "all 200ms ease",
                  transform: isHovered && !isSelected ? "translateY(-2px)" : "translateY(0)",
                  userSelect: "none",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {/* ── Subcategory icon ── */}
                <div
                  style={{
                    flexShrink: 0,
                    width: "64px",
                    height: "64px",
                    borderRadius: "12px",
                    background: isSelected ? "rgba(21,101,192,0.08)" : "rgba(0,0,0,0.03)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: isSelected ? "1px solid rgba(21,101,192,0.22)" : "1px solid #ddeef7",
                    transition: "all 200ms ease",
                    padding: "5px",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={item.image || noPic}
                    alt={item.name}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                  />
                </div>

                {/* ── Subcategory name ── */}
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "13.5px",
                    fontWeight: isSelected ? "700" : "600",
                    color: isSelected ? "#1565C0" : "#2a2a2a",
                    lineHeight: 1.35,
                    transition: "color 200ms ease",
                    wordBreak: "break-word",
                  }}
                >
                  {item.name}
                </Typography>

                {/* ── Radio ── */}
                <Radio
                  value={item.id}
                  checked={isSelected}
                  name="subcategory-radio"
                  inputProps={{ "aria-label": item.name }}
                  sx={{
                    padding: "2px",
                    flexShrink: 0,
                    color: "#c8c8c8",
                    "&.Mui-checked": { color: "#1565C0" },
                    "& .MuiSvgIcon-root": { fontSize: "21px" },
                    transition: "color 200ms ease",
                  }}
                />
              </div>
            );
          })}
        </Box>

        {/* ── Sticky footer — deeper blue for subcategory ── */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background: "linear-gradient(135deg, #1565C0 0%, #0d47a1 100%)",
            padding: "11px 16px",
            borderRadius: "0 0 22px 22px",
            mt: "8px",
          }}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.92)", textAlign: "center", fontSize: "11px", lineHeight: 1.5 }}>
            If what you are looking for is not available, please select "other sub category"
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
}

export default SubCategoryModal;
